import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import { LuSave } from "react-icons/lu";
import { Group, MoveLeft, MoveRight } from "lucide-react";
import InsurerDropdown from "../../../../utils/Insurer";
import CustomerDropdown from "../../../../utils/CustomerDropdown";
import AgencyDropdown from "../../../../utils/AgencyDropdown";
import ProductDropdown from "../../../../utils/ProductDropdown";
import PremiumInfoStep from "./PremiumInfoStep";
import { useSaveLifePolicyEntryMutation, useGetLifePolicyEntryByIdQuery } from "../../../../services/redux/api/components";
import { toast } from "react-toastify";
import { useLoader } from '../../../../context/LoaderContext';

const LifeInsuranceEntry = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [insurerType, setInsurerType] = useState("G");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { data: LifePolicyEntryData, isLoading } = useGetLifePolicyEntryByIdQuery(id, { skip: !isEditMode });
  console.log("Fetched LifePolicyEntryData:", LifePolicyEntryData);

  const { showLoader, hideLoader } = useLoader();

  // Show global loader while initial data is being fetched
  useEffect(() => {
    if (isLoading) showLoader();
    else hideLoader();
  }, [isLoading, showLoader, hideLoader]);

  // Helper: convert ISO date / timestamp to yyyy-mm-dd for date inputs
  const toInputDate = (d) => {
    if (!d) return "";
    try {
      const dt = d instanceof Date ? d : new Date(d);
      if (isNaN(dt.getTime())) return "";
      const yyyy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const dd = String(dt.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    } catch (e) {
      return "";
    }
  };

  // Build initial values when editing (map API shape -> form shape)
  const fetchedInitialValues = React.useMemo(() => {
    if (!isEditMode || !LifePolicyEntryData || !LifePolicyEntryData.data) return null;

    const policy = LifePolicyEntryData.data.policy || {};
    const bank = LifePolicyEntryData.data.bankDetails || {};
    const riders = Array.isArray(LifePolicyEntryData.data.riders)
      ? LifePolicyEntryData.data.riders
      : Array.isArray(LifePolicyEntryData.data.PolicyRiders)
        ? LifePolicyEntryData.data.PolicyRiders
        : Array.isArray(LifePolicyEntryData.data.policyRiders)
          ? LifePolicyEntryData.data.policyRiders
          : [];
    const cashFlows = Array.isArray(LifePolicyEntryData.data.cashFlows) ? LifePolicyEntryData.data.cashFlows : [];
    const deposits = Array.isArray(LifePolicyEntryData.data.deposits) ? LifePolicyEntryData.data.deposits : [];

    return {
      // basic policy fields
      PolicyId: policy.PolicyId ?? policy.PolicyID ?? null,
      Mode: 'UPDATE',
      // Show customer name in LifeAssured (if available). Store raw id into CustomerID below.
      LifeAssured: policy.LifeAssuredName ?? policy.LifeAssured ?? "",
      DOB: toInputDate(policy.DOB ?? policy.DOB),
      Age: policy.Age ?? "",
      PANNumber: policy.PANNumber ?? "",
      InsurerId: policy.InsurerId ?? policy.InsurerID ?? "",
      ProductId: policy.ProductId ?? policy.ProductID ?? "",
      productId: policy.ProductId ?? policy.ProductID ?? "",
      CommDate: toInputDate(policy.CommDate ?? policy.CommencementDate),
      PolicyNumber: policy.PolicyNumber ?? "",
      SumAssured: policy.SumAssured ?? "",
      Term: policy.Term ?? "",
      PPT: policy.PPT ?? "",
      CompletionDate: toInputDate(policy.CompletionDate),
      Agency: policy.Agency ?? "",
      PaymentMode: policy.PaymentMode ?? "",
      paymentMode: policy.PaymentMode ?? "",
      // insurer/product lowercase aliases
      insurerId: policy.InsurerId ?? policy.InsurerID ?? null,
      // also populate lowercase keys used by some child components
      paymentMode: policy.PaymentMode ?? "",
      BasicPremium: policy.BasicPremium ?? "",
      basicPremium: policy.BasicPremium ?? "",
      ExtraPremium: policy.ExtraPremium ?? "",
      ExtraPremium: policy.ExtraPremium ?? "",
      GSTFirstYear: policy.GSTFirstYear ?? null,
      GSTFirstYear: policy.GSTFirstYear ?? null,
      GSTSecondYearOnwards: policy.GSTSecondYearOnwards ?? null,
      TaxBeneficiary: policy.TaxBeneficiary ?? null,
      taxBeneficiary: policy.TaxBeneficiary ?? null,
      NomineeType: policy.NomineeType ?? "",
      NomineeName: policy.NomineeName ?? "",
      NomineeRelation: policy.NomineeRelation ?? "",
      PremiumPaidByNach: policy.PremiumPaidByNach ? true : false,
      generateDepositByEntry: policy.GenerateDepositByEntry ? 1 : 0,
      StatusDate: toInputDate(policy.StatusDate),
      PolicyStatus: policy.PolicyStatus ?? "",
      FUPDate: toInputDate(policy.FUPDate),
      TotalPremiumPaid: policy.TotalPremiumPaid ?? "",
      SurrenderValue: policy.SurrenderValue ?? "",
      LoanAvailable: policy.LoanAvailable ?? "",

      // bank details
      BankName: bank.BankName ?? "",
      AccountNumber: bank.AccountNumber ?? "",
      BankIFSC: bank.IFSCCode ?? bank.BankIFSC ?? "",
      BankBranch: bank.BankBranch ?? "",
      MandateEndDate: toInputDate(bank.MandateEndDate),
      DebitDay: bank.DebitDay ?? "",

      // arrays (map to form-friendly shape)
      cashFlows: cashFlows.map(cf => ({
        CashFlowDate: toInputDate(cf.CashFlowDate),
        CashFlowType: cf.CashFlowType || cf.cashFlowType || "",
        Amount: cf.Amount ?? cf.amount ?? "",
        Description: cf.Description ?? cf.description ?? "",
      })),

      riders: riders.map(r => ({
        RiderId: r.RiderId ?? r.RiderID ?? null,
        RiderName: r.RiderName ?? r.Rider_Name ?? r.name ?? "",
        SUM: r.SUM ?? r.sum ?? "",
        sum: r.SUM ?? r.sum ?? "",
        Term: r.Term ?? r.term ?? "",
        PPT: r.PPT ?? r.ppt ?? "",
        Premium: r.Premium ?? r.premium ?? "",
        premium: r.Premium ?? r.premium ?? "",
      })),

      PremiumDeposits: deposits.map(d => ({
        DepositId: d.DepositId ?? d.DepositID ?? null,
        PolicyId: d.PolicyId ?? null,
        PolicyNumber: d.PolicyNumber ?? "",
        DueDate: toInputDate(d.DueDate),
        PaymentDate: toInputDate(d.PaymentDate),
        PremiumAmount: d.PremiumAmount ?? d.Premium ?? d.Amount ?? null,
        GSTOnPremium: d.GSTOnPremium ?? 0,
        LateFeeAmount: d.LateFeeAmount ?? 0,
        GSTOnLateFee: d.GSTOnLateFee ?? 0,
        PaymentMode: d.PaymentMode ?? null,
        PaymentDetails: d.PaymentDetails ?? d.Description ?? null,
        Notes: d.Notes ?? null,

        // policy-level fields that your controller expects
        LifeAssured: d.LifeAssured ?? policy.LifeAssured ?? "",
        Product: d.Product ?? policy.Product ?? null,
        GroupCode: d.GroupCode ?? null,
        CommencementDate: toInputDate(d.CommencementDate ?? policy.CommDate ?? policy.CommencementDate),
        SumAssured: d.SumAssured ?? policy.SumAssured ?? null,
        TermPPT: d.TermPPT ?? (policy.Term != null ? String(policy.Term) : null),
        PPT: d.PPT ?? policy.PPT ?? null,
        Agency: d.Agency ?? policy.Agency ?? "",
      })),
      // keep the numeric customer id separate so the UI can display a name in LifeAssured
      CustomerID: policy.LifeAssured ?? policy.CustomerID ?? null,
    };
  }, [isEditMode, LifePolicyEntryData]);

  const [SaveLifePolicyEntry] = useSaveLifePolicyEntryMutation();

  const initialValues = {
    LifeAssured: "",
    DOB: "",
    Age: "",
    PANNumber: "",
    InsurerId: "",
    ProductId: "",
    RiderId: "",
    CommDate: "",
    PolicyNumber: "",
    Term: "",
    PPT: "",
    SumAssured: "",
    CompletionDate: "",
    Agency: "",
    PaymentMode: "",
    BasicPremium: "",
    ExtraPremium: "",
    GSTFirstYear: "",
    GSTSecondYearOnwards: "",
    TaxBeneficiary: "",
    NomineeType: "",
    NomineeName: "",
    NomineeRelation: "",
    PremiumPaidByNach: false,
    generateDepositByEntry: 0,
    BankName: "",
    AccountNumber: "",
    BankIFSC: "",
    BankBranch: "",
    MandateEndDate: "",
    DebitDay: "",
    CashFlowDate: "",
    GroupID: "",
    CashFlowType: "",
    Amount: "",
    Description: "",
    StatusDate: "",
    PolicyStatus: "",
    FUPDate: "",
    TotalPremiumPaid: "",
    SurrenderValue: "",
    LoanAvailable: "",
    CustomerID: null,
    cashFlows: [],
    riders: [], // use lowercase 'riders' to match PremiumInfoStep (riders[...].sum etc.)
    PremiumDeposits: []
  };

  const nextStep = () => setStep((s) => s + 1);
  const prevStep = () => setStep((s) => s - 1);

  const steps = [
    { title: "Policy Info" },
    { title: "Premium Info" },
    { title: "Nominee Info" },
    { title: "NACH/NEFT Info" },
    { title: "Cash Flow Info" },
    { title: "Current Status" },
  ];

  const handleSubmit = async (values, { resetForm }) => {
    // Helper to coerce boolean/0/1 for GenerateDepositByEntry
    const toBit = (v) => {
      if (v === true || v === 1 || v === '1') return 1;
      return 0;
    };
    const premiumPaidByNachBit = (values.PremiumPaidByNach === true || values.PremiumPaidByNach === 1 || values.PremiumPaidByNach === '1') ? 1 : 0;

    // Helper to coerce numeric-id strings to numbers (returns null for empty/invalid)
    const toNumber = (v) => {
      if (v === null || v === undefined || v === "") return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    // Map riders (formik uses lowercase 'riders')
    const policyRiders = (values.riders || [])
      .map((rider) => ({
        RiderName: rider.RiderName || rider.Rider_Name || rider.name || '',
        SUM: rider.sum ?? rider.SUM ?? null,
        Term: rider.term ?? rider.Term ?? null,
        PPT: rider.ppt ?? rider.PPT ?? null,
        Premium: rider.premium ?? rider.Premium ?? null,
      }))
      .filter(r => r.RiderName || r.SUM || r.Term || r.PPT || r.Premium);

    // Map cashFlows
    const lifeCashFlows = (values.cashFlows || []).map(flow => ({
      CashFlowDate: flow.CashFlowDate ?? flow.cashFlowDate ?? null,
      CashFlowType: flow.CashFlowType ?? flow.cashFlowType ?? null,
      Amount: flow.Amount ?? flow.amount ?? null,
      Description: flow.Description ?? flow.description ?? null
    }));

    // Map PremiumDeposits (client-provided). This mapping includes the extra policy-level fields
    const premiumDeposits = (values.PremiumDeposits || []).map(d => {
      // pick due/payment dates from multiple possible keys
      const dueDate = d.DueDate || d.dueDate || d.DepositDate || d.depositDate || values.CommDate || values.StatusDate || null;
      const paymentDate = d.PaymentDate || d.paymentDate || null;

      // pick amounts
      const premiumAmount = (d.PremiumAmount ?? d.Amount ?? d.amount ?? values.BasicPremium ?? values.TotalPremiumPaid ?? null);

      return {
        PolicyId: d.PolicyId ?? null,
        PolicyNumber: d.PolicyNumber ?? values.PolicyNumber ?? null,
        DueDate: dueDate ?? null,
        PaymentDate: paymentDate ?? null,
        PremiumAmount: premiumAmount ?? null,
        GSTOnPremium: d.GSTOnPremium ?? d.GST ?? 0,
        LateFeeAmount: d.LateFeeAmount ?? 0,
        GSTOnLateFee: d.GSTOnLateFee ?? 0,
        PaymentMode: d.PaymentMode ?? values.PaymentMode ?? null,
        PaymentDetails: d.PaymentDetails ?? d.Description ?? d.description ?? (values.AccountNumber ? `Acct:${values.AccountNumber}` : null),
        Notes: d.Notes ?? d.Note ?? null,
  
        // *** Policy-level fields to be stored into Premium_Deposit_Entry as requested ***
        // Keep LifeAssured as the display name; preserve numeric ids in CustomerID/Product where needed
        LifeAssured: d.LifeAssured ?? values.LifeAssured ?? null,
        Product: toNumber(d.Product ?? values.ProductId ?? values.Product ?? null),     // controller can map ProductId to name if needed
        GroupCode: d.GroupCode ?? values.GroupCode ?? null,
        CommencementDate: d.CommencementDate ?? d.CommDate ?? values.CommDate ?? null,
        SumAssured: d.SumAssured ?? values.SumAssured ?? null,
        TermPPT: d.TermPPT ?? (values.Term != null ? String(values.Term) : null),
        PPT: d.PPT ?? values.PPT ?? null,
        Agency: toNumber(d.Agency ?? values.Agency ?? null) ?? (d.Agency ?? values.Agency ?? null),
        GroupID: toNumber(d.GroupID ?? values.GroupID ?? null),
        CustomerID: toNumber(d.CustomerID ?? values.CustomerID ?? null),
      };
    });

    // If client didn't supply PremiumDeposits but generateDepositByEntry is true, we don't need to populate
    // anything here — server will generate deposit(s) from LifePolicyEntry if GenerateDepositByEntry = 1.
    // But if you want to send an empty array explicitly:
    // const depositsToSend = premiumDeposits.length ? premiumDeposits : [];

    const payload = {
      Mode: isEditMode ? 'UPDATE' : (values.Mode || "ADD"),
      LifePolicyEntry: {
        PolicyId: isEditMode ? (values.PolicyId ?? values.PolicyID ?? values.PolicyId) : (values.PolicyId ?? null),
        // basic policy fields (PascalCase to match controller)
        // LifeAssured will contain the customer's display name (string). The numeric id is sent separately
        // in `CustomerID` so backend has both pieces of information if it needs them.
        LifeAssured: values.LifeAssured ?? null,
        DOB: values.DOB ?? null,
        Age: values.Age ?? null,
        PANNumber: values.PANNumber ?? null,
        // convert dropdown/select values which come from HTML as strings into numbers
        InsurerId: toNumber(values.InsurerId ?? values.insurerId ?? null),
        ProductId: toNumber(values.ProductId ?? values.productId ?? null),
        RiderId: toNumber(values.RiderId ?? null),
        CommDate: values.CommDate ?? null,
        PolicyNumber: values.PolicyNumber ?? null,
        Term: values.Term ?? null,
        PPT: values.PPT ?? null,
        SumAssured: values.SumAssured ?? null,
        CompletionDate: values.CompletionDate ?? null,
        Agency: values.Agency ?? null,
        PaymentMode: values.PaymentMode ?? null,
        BasicPremium: values.BasicPremium ?? null,
        ExtraPremium: values.ExtraPremium ?? null,
        PremiumPaidByNach: premiumPaidByNachBit,
        // normalize GST names to backend
        GSTFirstYear: values.GSTFirstYear ?? values.GSTFirstYear ?? null,
        GSTSecondYearOnwards: values.GSTSecondYearOnwards ?? values.GSTSecondYearOnwards ?? null,

        TaxBeneficiary: values.TaxBeneficiary ?? null,
        NomineeType: values.NomineeType ?? null,
        NomineeName: values.NomineeName ?? null,
        NomineeRelation: values.NomineeRelation ?? null,
        StatusDate: values.StatusDate ?? null,
        PolicyStatus: values.PolicyStatus ?? null,
        FUPDate: values.FUPDate ?? null,
        TotalPremiumPaid: values.TotalPremiumPaid ?? null,
        SurrenderValue: values.SurrenderValue ?? null,
        LoanAvailable: values.LoanAvailable ?? null,
        GroupID: values.GroupID ?? null,
        // include numeric customer id (if present) so backend receives the FK as well
        CustomerID: toNumber(values.CustomerID ?? values.CustomerId ?? null),
        CustomerID: values.CustomerID ?? null,

        // Important: match controller param name exactly
        GenerateDepositByEntry: toBit(values.generateDepositByEntry ?? values.GenerateDepositByEntry),
      },

      // Bank section at root (controller reads data.LifePolicyBankDetails or policy)
      LifePolicyBankDetails: values.PremiumPaidByNach ? {
        BankName: values.BankName ?? null,
        AccountNumber: values.AccountNumber ?? null,
        IFSCCode: values.BankIFSC ?? values.IFSC ?? null, // controller tolerates IFSC/BankIFSC
        BankBranch: values.BankBranch ?? null,
        MandateEndDate: values.MandateEndDate ?? null,
        DebitDay: values.DebitDay ?? null
      } : null,

      // arrays
      PolicyRiders: policyRiders,
      LifeCashFlows: lifeCashFlows,
      // If the user provided premium deposits, send them; otherwise send empty array and let
      // server generate deposits when GenerateDepositByEntry=1.
      PremiumDeposits: premiumDeposits.length ? premiumDeposits : []
    };

    console.log("Payload being sent to backend:", payload);

    try {
      showLoader();
      const response = await SaveLifePolicyEntry(payload).unwrap();
      if (response?.Status === "SUCCESS" || response?.success === true) {
        toast.success(response?.Message || "Life Policy Entry saved successfully");
        resetForm();
        window.history.back();
      } else {
        toast.error(response?.Message || "Failed to save Leife Policy Entry");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error(
        error?.data?.Message || error?.message || "An error occurred while saving Life Policy Entry"
      );
    } finally {
      hideLoader();
    }
  };

  return (
    <div className="formContainer lg:p-4 rounded" style={{ boxShadow: "0 0 8px #ccc" }}>
      <div
        className="flex flex-col lg:mx-5 p-4 border rounded bg-white"
        style={{ boxShadow: "0px 0px 12px #cac8c8" }}
      >
        <h6 className="text-brandRed font-bold border-b border-brandRed pb-2 mb-4 px-2">
          Life Insurance Entry
        </h6>
        {/* Stepper Header */}
        <div className="d-flex justify-content-between align-items-center mb-5">
          {steps.map((stepItem, index) => {
            const current = index + 1;
            const isCompleted = step > current;
            const isActive = step === current;
            return (
              <React.Fragment key={index}>
                <div
                  className="text-center d-flex flex-column align-items-center"
                  style={{ minWidth: "100px" }}
                >
                  <div
                    className="rounded-circle d-flex justify-content-center align-items-center mb-1"
                    style={{
                      width: "32px",
                      height: "32px",
                      backgroundColor: isCompleted || isActive ? "#0d6efd" : "#fff",
                      border: `2px solid ${isCompleted || isActive ? "#0d6efd" : "#dc3545"}`,
                      color: isCompleted || isActive ? "#fff" : "#dc3545",
                      fontWeight: "bold",
                    }}
                  >
                    {isCompleted ? "✓" : current}
                  </div>
                  <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {stepItem.title}
                  </div>
                </div>
                {index !== steps.length - 1 && (
                  <div
                    className="flex-grow-1"
                    style={{
                      height: "2px",
                      backgroundColor: step > current ? "#0d6efd" : "#ddd",
                      margin: "0 8px",
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
        {/* Formik Start */}
        <Formik
          initialValues={fetchedInitialValues || initialValues}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values }) => (
            <Form>
              {/* Step 1 */}
              {step === 1 && (
                <>
                  <h6 className="text-brandRed border-bottom pb-2 mb-4">
                    Policy Information
                  </h6>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <CustomerDropdown
                        name="LifeAssured"
                        className="form-control custom-shadow"
                        onCustomerSelect={(customer) => {
                          console.log("hyyy", customer)
                          try {
                            const isoDob = customer?.DOB ? String(customer.DOB) : "";
                            const dobDate = isoDob ? new Date(isoDob) : null;
                            const yyyy = dobDate ? dobDate.getFullYear() : "";
                            const mm = dobDate
                              ? String(dobDate.getMonth() + 1).padStart(2, "0")
                              : "";
                            const dd = dobDate
                              ? String(dobDate.getDate()).padStart(2, "0")
                              : "";
                            const dobForInput = dobDate ? `${yyyy}-${mm}-${dd}` : "";
                            const calcAge = (dateObj) => {
                              if (!dateObj) return "";
                              const today = new Date();
                              let age = today.getFullYear() - dateObj.getFullYear();
                              const m = today.getMonth() - dateObj.getMonth();
                              if (m < 0 || (m === 0 && today.getDate() < dateObj.getDate())) {
                                age--;
                              }
                              return String(age);
                            };
                            setFieldValue("DOB", dobForInput);
                            setFieldValue("Age", calcAge(dobDate));
                            setFieldValue(
                              "PANNumber",
                              customer?.PAN ?? customer?.Pan ?? ""
                            );
                            setFieldValue("GroupID", customer?.GroupID ?? "");
                            setFieldValue("CustomerID", customer?.CustomerID ?? customer?.id ?? "");
                            setFieldValue("lifeAssured", customer?.FullName ?? customer?.id ?? "");
                          } catch (e) { }
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">DOB</label>
                      <Field type="date" name="DOB" className="form-control custom-shadow" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Age</label>
                      <Field
                        type="number"
                        name="Age"
                        className="form-control custom-shadow"
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">PAN Number</label>
                      <Field
                        type="text"
                        name="PANNumber"
                        className="form-control custom-shadow"
                      />
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <InsurerDropdown
                        name="InsurerId"
                        // insurerType={insurerType}
                        className="form-control custom-shadow"
                      />
                    </div>
                    <div className="col-md-4">
                      <ProductDropdown
                        name="ProductId"
                        onProductSelect={(product) => {
                          console.log("Selected Product object:", product);
                          setSelectedProduct(product);
                          try {
                            const id = product?.id ?? product?.ProductID ?? product;
                            setFieldValue && setFieldValue("ProductId", id);
                          } catch (e) {
                            console.error("Failed to set ProductId in Formik:", e);
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Comm. Date</label>
                      <Field type="date" name="CommDate" className="form-control custom-shadow" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Policy Number</label>
                      <Field type="text" name="PolicyNumber" className="form-control custom-shadow" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Sum Assured</label>
                      <Field type="number" name="SumAssured" className="form-control custom-shadow" />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Term</label>
                      <Field
                        as="select"
                        name="Term"
                        className="form-control custom-shadow"
                      >
                        <option value="">Select Term</option>
                        <option value="10">10</option>
                        <option value="15">15</option>
                        <option value="20">20</option>
                        <option value="25">25</option>
                        <option value="55">55</option>
                      </Field>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">PPT</label>
                      <Field
                        as="select"
                        name="PPT"
                        className="form-control custom-shadow"
                      >
                        <option value="">Select PPT</option>
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="15">15</option>
                        <option value="20">20</option>
                        <option value="55">55</option>
                      </Field>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Completion Date</label>
                      <Field type="date" name="CompletionDate" className="form-control custom-shadow" />
                    </div>
                  </div>
                  <div className="mb-3 col-lg-4">
                    <AgencyDropdown className="form-control custom-shadow" name="Agency" />
                  </div>
                </>
              )}
              {/* Step 2 */}
              {step === 2 && (
                <PremiumInfoStep
                  insurerId={values.InsurerId}
                  productId={values.ProductId}
                  setFieldValue={setFieldValue}
                  values={values}
                />
              )}
              {/* Step 3 */}
              {step === 3 && (
                <>
                  <h6 className="text-brandRed border-bottom pb-2 mb-4">Nominee Information</h6>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Nominee Type</label>
                      <Field
                        as="select"
                        name="NomineeType"
                        className="form-control custom-shadow"
                      >
                        <option value="">Select Nominee Type</option>
                        <option value="Single Nominee">Single Nominee</option>
                        <option value="Successive Nominee">Successive Nominee</option>
                        <option value="Multiple Nominee">Multiple Nominee</option>
                        <option value="Assignee">Assignee</option>
                        <option value="Beneficiary">Beneficiary</option>
                        <option value="Proposer">Proposer</option>
                      </Field>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Nominee Name</label>
                      <Field type="text" name="NomineeName" className="form-control custom-shadow" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Nominee Relation</label>
                      <Field
                        as="select"
                        name="NomineeRelation"
                        className="form-control custom-shadow"
                      >
                        <option value="">Select Nominee Relation</option>
                        <option value="Father">Father</option>
                        <option value="Mother">Mother</option>
                        <option value="Wife">Wife</option>
                        <option value="Spouse">Spouse</option>
                        <option value="Son">Son</option>
                        <option value="Daughter">Daughter</option>
                        <option value="Brother">Brother</option>
                        <option value="Sister">Sister</option>
                        <option value="Grandfather">Grandfather</option>
                        <option value="Grandmother">Grandmother</option>
                        <option value="Uncle">Uncle</option>
                        <option value="Aunt">Aunt</option>
                        <option value="Father-in-law">Father-in-law</option>
                        <option value="Mother-in-law">Mother-in-law</option>
                        <option value="Son-in-law">Son-in-law</option>
                        <option value="Daughter-in-law">Daughter-in-law</option>
                        <option value="Other">Other</option>
                        <option value="Employee">Employee</option>
                        <option value="Self">Self</option>
                      </Field>
                    </div>
                  </div>
                </>
              )}
              {/* Step 4 */}
              {step === 4 && (
                <>
                  <h6 className="text-brandRed border-bottom pb-2 mb-4">NACH Information</h6>
                  <div className="mb-3 form-check">
                    <Field type="checkbox" name="PremiumPaidByNach" className="form-check-input" />
                    <label className="form-check-label">Premium Paid By Nach</label>
                  </div>
                  {values?.PremiumPaidByNach === true && (
                    <>
                      <div className="row mb-3">
                        <div className="col-md-4">
                          <label className="form-label">Bank Name</label>
                          <Field type="text" name="BankName" className="form-control custom-shadow" />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Account Number</label>
                          <Field type="text" name="AccountNumber" className="form-control custom-shadow" />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Bank IFSC</label>
                          <Field type="text" name="BankIFSC" className="form-control custom-shadow" />
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-4">
                          <label className="form-label">Bank Branch</label>
                          <Field type="text" name="BankBranch" className="form-control custom-shadow" />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Mandate End Date</label>
                          <Field type="date" name="MandateEndDate" className="form-control custom-shadow" />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Debit Day</label>
                          <Field
                            as="select"
                            name="DebitDay"
                            className="form-control custom-shadow"
                          >
                            <option value="">Select Debit Day</option>
                            <option value="7">7th</option>
                            <option value="15">15th</option>
                            <option value="22">22th</option>
                            <option value="28">28th</option>
                          </Field>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              {/* Step 5 */}
              {step === 5 && (
                <>
                  <h6 className="text-brandRed border-bottom pb-2 mb-4">
                    Cash Flow Information
                  </h6>
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <label className="form-label">Cash Flow Date</label>
                      <Field
                        type="date"
                        name="CashFlowDate"
                        className="form-control custom-shadow"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Cash Flow Type</label>
                      <Field
                        as="select"
                        name="CashFlowType"
                        className="form-control custom-shadow"
                      >
                        <option value="">-- Select Cash Flow Type --</option>
                        <option value="Premium Payment">Premium Payment</option>
                        <option value="Bonus">Bonus</option>
                        <option value="Commission">Commission</option>
                        <option value="Surrender Value">Surrender Value</option>
                        <option value="Maturity Value">Maturity Value</option>
                        <option value="Death Claim">Death Claim</option>
                        <option value="Partial Withdrawal">Partial Withdrawal</option>
                        <option value="Loan Repayment">Loan Repayment</option>
                      </Field>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Amount</label>
                      <Field
                        type="number"
                        name="Amount"
                        className="form-control custom-shadow"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Description</label>
                      <Field
                        type="text"
                        name="Description"
                        className="form-control custom-shadow"
                      />
                    </div>
                  </div>
                  <div className="mb-3 text-center">
                    <button
                      type="button"
                      className="saveButton m-2"
                      style={{ boxShadow: "0 4px 6px rgba(0,0,0,0.2)" }}
                      onClick={() => {
                        const { CashFlowDate, CashFlowType, Amount, Description } =
                          values;
                        if (!CashFlowDate || !CashFlowType || !Amount) {
                          alert("Please fill all required fields before adding cashflow.");
                          return;
                        }
                        const newCashflow = {
                          CashFlowDate,
                          CashFlowType,
                          Amount,
                          Description,
                        };
                        setFieldValue("cashFlows", [...values.cashFlows, newCashflow]);
                        setFieldValue("CashFlowDate", "");
                        setFieldValue("CashFlowType", "");
                        setFieldValue("Amount", "");
                        setFieldValue("Description", "");
                      }}
                    >
                      ADD Cashflow
                    </button>
                  </div>
                  {/* Cashflow Table */}
                  {values.cashFlows && values.cashFlows.length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-bordered text-center">
                        <thead className="table-light">
                          <tr>
                            <th>Cash Flow Date</th>
                            <th>Cash Flow Type</th>
                            <th>Amount</th>
                            <th>Description</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {values.cashFlows.map((flow, index) => (
                            <tr key={index}>
                              <td>{flow.CashFlowDate}</td>
                              <td>{flow.CashFlowType}</td>
                              <td>{flow.Amount}</td>
                              <td>{flow.Description}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => {
                                    const updated = values.cashFlows.filter(
                                      (_, i) => i !== index
                                    );
                                    setFieldValue("cashFlows", updated);
                                  }}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
              {/* Step 6 */}
              {step === 6 && (
                <>
                  <h6 className="text-brandRed border-bottom pb-2 mb-4">Current Status Information</h6>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Status Date</label>
                      <Field type="date" name="StatusDate" className="form-control custom-shadow" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Policy Status</label>
                      <Field
                        as="select"
                        name="PolicyStatus"
                        className="form-control custom-shadow"
                      >
                        <option value="">Select Policy Status</option>
                        <option value="In Force">In Force</option>
                        <option value="Lapsed">Lapsed</option>
                        <option value="Fully Paid-up">Fully Paid-up</option>
                        <option value="Reduced Paid-up">Reduced Paid-up</option>
                        <option value="Surrendered">Surrendered</option>
                        <option value="Matured">Matured</option>
                        <option value="Death Claim">Death Claim</option>
                        <option value="Cooling Off Cancellation">Cooling Off Cancellation</option>
                        <option value="Lapsed without Surrender Value">Lapsed without Surrender Value</option>
                        <option value="Terminated">Terminated</option>
                      </Field>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">FUP Date</label>
                      <Field type="date" name="FUPDate" className="form-control custom-shadow" />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Total Premium Paid</label>
                      <Field type="number" name="TotalPremiumPaid" className="form-control custom-shadow" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Surrender Value</label>
                      <Field type="number" name="SurrenderValue" className="form-control custom-shadow" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Loan Available</label>
                      <Field type="text" name="LoanAvailable" className="form-control custom-shadow" />
                    </div>
                    <div className="mb-3 form-check">
                      <Field
                        type="checkbox"
                        name="generateDepositByEntry"
                        className="form-check-input"
                        onChange={(e) => setFieldValue("generateDepositByEntry", e.target.checked ? 1 : 0)}
                      />
                      <label className="form-check-label">Generate Deposit By Entry</label>
                    </div>
                  </div>
                </>
              )}
              {/* Navigation Buttons */}
              {/* Navigation Buttons */}
              <div className="d-flex align-items-center mt-4">
                {step > 1 && (
                  <button
                    type="button"
                    className="backButton d-flex align-items-center px-4"
                    onClick={prevStep}
                  >
                    <MoveLeft className="me-2" size={15} />
                    Previous
                  </button>
                )}
                {step < 6 && (
                  <button
                    type="button"
                    className="saveButton d-flex align-items-center px-4 ms-auto"
                    onClick={nextStep}
                  >
                    Next
                    <MoveRight size={15} className="me-2 ml-2" />
                  </button>
                )}
                {step === 6 && (
                  <button
                    type="submit"
                    className="saveButton d-flex align-items-center px-4 ms-auto"
                    disabled={isSubmitting}
                  >
                    <LuSave className="me-2" />
                    {isSubmitting
                      ? (isEditMode ? "Updating..." : "Saving...")
                      : (isEditMode ? "Update" : "Add")}
                  </button>
                )}
              </div>

            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default LifeInsuranceEntry;
